import type { CatalogImage } from '@numistic/contracts'
import { useTranslation } from '@numistic/i18n'
import { cn } from '@numistic/ui'
import { useState } from 'react'

import { Lightbox } from './lightbox.js'

type GalleryImage = Pick<CatalogImage, 'id' | 'role' | 'urls' | 'width' | 'height'>

/**
 * Галерея фото предмета: крупное фото (облегчённая копия), миниатюры, по клику — просмотр
 * поверх страницы с зумом. Одна на каталог, экземпляр и публичную ссылку.
 */
export function ImageGallery({
  images,
  title,
  note,
  className,
}: {
  images: GalleryImage[]
  title: string
  /** Подпись под фото (например, «Фото из каталога»). */
  note?: string
  className?: string
}) {
  const { t } = useTranslation()
  const [current, setCurrent] = useState(0)
  const [open, setOpen] = useState(false)
  const index = Math.min(current, Math.max(0, images.length - 1))
  const image = images[index]
  const roleName = (img: GalleryImage) => t(`catalog.images.roles.${img.role}`)

  if (!image)
    return (
      <div
        className={cn(
          'flex aspect-square items-center justify-center rounded-lg border border-dashed bg-muted/40 text-sm text-muted-foreground',
          className,
        )}
      >
        {t('catalog.noImage')}
      </div>
    )

  return (
    <div className={cn('grid content-start gap-3', className)}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group relative block overflow-hidden rounded-lg border bg-muted focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={t('lightbox.open', { name: `${title} — ${roleName(image)}` })}
      >
        <img
          src={image.urls.display}
          alt={`${title} — ${roleName(image)}`}
          width={image.width}
          height={image.height}
          className="mx-auto max-h-[28rem] w-auto object-contain transition-transform duration-200 group-hover:scale-[1.02]"
        />
        <span className="pointer-events-none absolute right-2 bottom-2 rounded-full bg-black/60 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          {t('lightbox.zoomHint')}
        </span>
      </button>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      {images.length > 1 ? (
        <ul className="flex flex-wrap gap-2" aria-label={t('catalog.images.title')}>
          {images.map((img, i) => (
            <li key={img.id}>
              <button
                type="button"
                aria-label={roleName(img)}
                aria-current={i === index}
                onClick={() => setCurrent(i)}
                onDoubleClick={() => {
                  setCurrent(i)
                  setOpen(true)
                }}
                className="size-16 overflow-hidden rounded-md border bg-muted transition-shadow aria-[current=true]:ring-2 aria-[current=true]:ring-primary"
              >
                <img
                  src={img.urls.thumb}
                  alt=""
                  loading="lazy"
                  className="size-full object-contain"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open ? (
        <Lightbox
          images={images.map((img) => ({
            id: img.id,
            display: img.urls.display,
            original: img.urls.original,
            alt: `${title} — ${roleName(img)}`,
            caption: `${roleName(img)} · ${title}`,
          }))}
          index={index}
          onIndexChange={setCurrent}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  )
}
